/**
 * Рассылка объявлений врачам.
 *
 * Общее письмо всем сразу: новые сроки, переезд, изменение цен. Устроено тем
 * же окном, что отправка прайса и карты цветов, — список получателей, тема,
 * текст. Вложения нет: это объявление, а не документ.
 *
 * Прежде у рассылки было своё окно со своим списком и своими кнопками. Два
 * похожих, но разных окна для одного и того же действия — «выбрать врачей и
 * написать им» — каждый раз заставляли вспоминать, где что лежит.
 *
 * Письма уходят ПО ОДНОМУ, каждому свой адрес: общий список получателей в
 * шапке показал бы каждому врачу почту всех остальных.
 */

import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { SendDocDialog, type SendTarget } from './SendDocDialog.js';

export interface MailingDoctor {
  id: string;
  name: string;
  email: string;
  /** Подпись под именем: клиники врача. */
  clinics?: string;
}

/** Строка истории: что и когда уже рассылали. */
interface MailingRow {
  id: string;
  subject: string;
  sentAt: string;
  total: number;
  failed: number;
}

/**
 * Заготовка письма: приветствие и подпись, середину пишет человек.
 *
 * Подпись — «зуботехническая лаборатория OrthoTech», как в письмах с
 * прайс-листом, нарядом и картой цветов. Раньше сюда подставлялось краткое
 * имя из реквизитов — «ИП Ханеев Д.О.»: это название для счёта и акта, а
 * врачу лаборатория известна по имени, а не по форме собственности.
 */
function заготовка(): string {
  return 'Здравствуйте!\n\n\n\nС уважением,\nзуботехническая лаборатория OrthoTech';
}

export function MailingDialog({
  doctors,
  onClose,
  onDone,
}: {
  doctors: MailingDoctor[];
  onClose: () => void;
  /** Рассылка завершилась — сколько ушло и сколько нет. */
  onDone: (sent: number, failed: number) => void;
}) {
  /*
   * История прошлых рассылок.
   *
   * Без неё через месяц не вспомнить, разослали объявление о новых сроках или
   * только собирались, — и оно уходит вторично. Три последних строки отвечают
   * на этот вопрос, не занимая места.
   */
  const [history, setHistory] = useState<MailingRow[]>([]);

  useEffect(() => {
    let живо = true;
    api
      .get<{ mailings: MailingRow[] }>('/api/mailings')
      // Молча: история — подсказка, а не условие работы.
      .then((r) => живо && setHistory(r.mailings.slice(0, 3)))
      .catch(() => undefined);
    return () => {
      живо = false;
    };
  }, []);

  const получатели: SendTarget[] = doctors.map((д) => ({
    id: д.id,
    name: д.name,
    email: д.email,
    note: д.clinics,
  }));

  return (
    <SendDocDialog
      title="Рассылка врачам"
      doctors={получатели}
      subject=""
      body={заготовка()}
      hint={
        history.length > 0 ? (
          <p className="mb-3 text-[11px] text-ink2">
            Последние рассылки:{' '}
            {history
              .map((м) => `«${м.subject}» — ${дата(м.sentAt)}${м.failed ? `, не дошло ${м.failed}` : ''}`)
              .join(' · ')}
          </p>
        ) : undefined
      }
      onClose={onClose}
      onDone={() => undefined}
      onSend={async (кому) => {
        /* Рассылка сперва записывается, потом отправляется: так она попадает
           в историю даже если часть писем не уйдёт. */
        const { id } = await api.post<{ id: string }>('/api/mailings', {
          subject: кому.subject,
          body: кому.body,
        });
        const results: {name:string;email:string;ok:boolean;error:string}[]=[];
        for(let offset=0;offset<Math.max(кому.doctorIds.length,кому.emails.length);offset+=25){
          const batch=await api.post<{results:typeof results}>(`/api/mailings/${id}/send`,{doctorIds:кому.doctorIds.slice(offset,offset+25),emails:кому.emails.slice(offset,offset+25)});
          results.push(...batch.results);
        }
        const ответ={results};
        const ушло = ответ.results.filter((р) => р.ok).length;
        onDone(ушло, ответ.results.length - ушло);
        return { sent: ушло, results: ответ.results };
      }}
    />
  );
}

/** `2026-09-25T…` → `25.09.2026`. */
function дата(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}
